import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

export interface PwaInstallState {
  canInstall: boolean;
  isInstalled: boolean;
  isIos: boolean;
  isStandalone: boolean;
  dismissedUntil: number;
}

@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private readonly platformId = inject(PLATFORM_ID);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private readonly storageKey = 'nibni_pwa_install_dismissed_until';

  private readonly stateSubject = new BehaviorSubject<PwaInstallState>({
    canInstall: false,
    isInstalled: false,
    isIos: false,
    isStandalone: false,
    dismissedUntil: 0
  });

  readonly state$ = this.stateSubject.asObservable();

  constructor() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.refreshState();

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.refreshState();
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.refreshState(true);
    });

    window.matchMedia('(display-mode: standalone)').addEventListener('change', () => this.refreshState());
  }

  dismiss(days = 7) {
    if (!isPlatformBrowser(this.platformId)) return;
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    try {
      window.localStorage.setItem(this.storageKey, String(until));
    } catch { }
    this.refreshState();
  }

  async promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
    if (!isPlatformBrowser(this.platformId)) return 'unavailable';
    if (!this.deferredPrompt) return 'unavailable';

    try {
      await this.deferredPrompt.prompt();
      const choice = await this.deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        this.deferredPrompt = null;
      }
      this.refreshState();
      return choice.outcome;
    } catch {
      this.refreshState();
      return 'unavailable';
    }
  }

  private refreshState(forceInstalled = false) {
    if (!isPlatformBrowser(this.platformId)) return;

    const ua = navigator.userAgent || '';
    const isIos = /iphone|ipad|ipod/i.test(ua);
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;

    const dismissedUntil = this.readDismissedUntil();
    const canInstall = !!this.deferredPrompt;

    this.stateSubject.next({
      canInstall,
      isInstalled: forceInstalled ? true : isStandalone,
      isIos,
      isStandalone,
      dismissedUntil
    });
  }

  private readDismissedUntil(): number {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      const value = Number(raw);
      return Number.isFinite(value) ? value : 0;
    } catch {
      return 0;
    }
  }
}

